import { apiClient } from './client';
import { AgentProfile } from '../types/agent.types';

export interface LoginCredentials {
  username: string;
  password: string;
}

// Respuesta cuando se requiere código 2FA
export interface Login2FAResponse {
  requiresCode: true;
  message: string;
  email: string;  // Email parcialmente oculto (a***@...)
}

// Respuesta exitosa con tokens
export interface LoginSuccessResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  user: {
    id: string;
    username: string;
    email: string;
    phone?: string | null;
    firstName: string;
    lastName: string;
    roles: string[];  // 🆕 Multi-role support
    role?: string;    // ✅ Backward compatibility
    kycStatus: string;
    kycLevel: number;
    isActive: boolean;
  };
}

// Union type para respuesta de login
export type LoginResponse = Login2FAResponse | LoginSuccessResponse;

// Request para verificar código
export interface VerifyCodeRequest {
  username: string; // Username del agente (según documentación panel-specific)
  code: string;     // Código de 6 dígitos
}

// Respuesta de GET /agent/me/profile
export interface AgentMeProfile {
  id: string;
  agentCode: string;
  businessName: string;
  businessType: string;
  status: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    phone: string;
  };
  commissionRate: number;
  stats: {
    totalTransactions: number;
    totalVolume: number;
    totalCommissions: number;
    avgTransactionSize: number;
    monthly: { transactions: number; volume: number; commissions: number };
  };
  float: {
    currentBalance: number;
    minBalance: number;
    maxBalance: number;
    status: string;
    currency: string;
  };
  kycVerified: boolean;
  kycStatus: string;
  kycLevel: number;
  address?: string;
  city?: string;
  state?: string;
  neighborhood?: string;
  rating?: number | null;
  reviewCount?: number;
}

export const agentApi = {
  // Autenticación - Paso 1: Login con contraseña (endpoint específico de agente)
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const { data } = await apiClient.post('/auth/agent/login', {
      username: credentials.username,
      password: credentials.password,
    });
    return data;
  },

  // Autenticación - Paso 2: Verificar código 2FA (endpoint específico de agente)
  verifyLoginCode: async (request: VerifyCodeRequest): Promise<LoginSuccessResponse> => {
    const { data } = await apiClient.post('/auth/agent/verify-code', request);
    return data;
  },

  // Helper para verificar si la respuesta requiere 2FA
  requires2FA: (response: LoginResponse): response is Login2FAResponse => {
    return 'requiresCode' in response && response.requiresCode === true;
  },

  // Obtener perfil del agente (endpoint completo con stats, float, user)
  getAgentProfile: async (): Promise<AgentMeProfile> => {
    const { data } = await apiClient.get('/agent/me/profile');
    return data?.data ?? data;
  },

  // Obtener perfil del agente (legacy)
  getProfile: async (): Promise<AgentProfile> => {
    const { data } = await apiClient.get('/agent/profile');
    return data;
  },

  // Actualizar perfil
  updateProfile: async (updates: Partial<AgentProfile['agent']>) => {
    const { data } = await apiClient.patch('/agent/profile', updates);
    return data;
  },

  // Obtener estadísticas
  getStatistics: async (period: 'daily' | 'weekly' | 'monthly') => {
    const { data } = await apiClient.get(`/agent/statistics?period=${period}`);
    return data;
  },
};
